import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { IdentityServiceBase, StoredAccountInfo } from '@seahorse/auth';
import {
  ShButtonComponent,
  ShPopoverComponent,
  ShToggleComponent,
} from '@seahorse/components';
import { ThemeModeService } from '@seahorse/theme';
import { DialogService } from '../dialog/dialog.service';
import {
  accountInitials,
  accountInitialsFromFullName,
} from '../models/account-initials';

/**
 * The account button's popover (design handoff, "Step 3, streamlined",
 * section 3). `sh-app-shell` owns the account button and calls `toggle()`
 * here to open/close it -- the popover itself has no visible trigger.
 *
 * Uses `IdentityServiceBase` from `@seahorse/auth`, the same service the
 * customer app's header already uses for account switching, so this works
 * unchanged in every app.
 */
@Component({
  selector: 'sh-account-panel',
  templateUrl: './account-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    ShButtonComponent,
    ShToggleComponent,
    ShPopoverComponent,
  ],
})
export class AccountPanelComponent {
  // Absent: "My profile" is hidden (see the brief -- super-user has no
  // active profile route today).
  profileRoute = input<string | unknown[] | undefined>(undefined);
  loginRoute = input<unknown[]>(['/account/login']);
  // Opt-in "new look" switch (ThemeModeService). Off by default: super-user
  // always runs the new look and doesn't enable it; the customer apps may
  // turn it on at the go-wide step.
  showThemeToggle = input(false);

  protected readonly identityService = inject(IdentityServiceBase);
  protected readonly themeMode = inject(ThemeModeService);
  private readonly _router = inject(Router);
  private readonly _dialogService = inject(DialogService);

  @ViewChild(ShPopoverComponent) private _popover?: ShPopoverComponent;

  otherAccounts: StoredAccountInfo[] = [];

  toggle(event: Event): void {
    this._popover?.toggle(event);
  }

  hide(): void {
    this._popover?.hide();
  }

  onShow(): void {
    const currentToken = this.identityService.token;
    this.otherAccounts = this.identityService
      .getAllAccountsInfo()
      .filter((account) => account.token !== currentToken);
  }

  initialsForCurrent(): string {
    return accountInitials(
      this.identityService.identity?.givenName,
      this.identityService.identity?.surname
    );
  }

  initialsFor(account: StoredAccountInfo): string {
    return accountInitialsFromFullName(account.fullName);
  }

  // Same behaviour as the customer app's header: switch and reload
  // (IdentityServiceBase.setIdentity(token, true) calls
  // window.location.replace('/') itself).
  switchToAccount(account: StoredAccountInfo): void {
    this.identityService.setIdentity(account.token, true);
    this.hide();
  }

  addAccount(): void {
    this.hide();
    this._router.navigate(this.loginRoute());
  }

  // Same behaviour as the customer app's header (HeaderComponent.logOut):
  // confirm first only when other accounts are still logged in.
  logOut(): void {
    if (!this.otherAccounts.length) {
      this.identityService.clearIdentity();
      return;
    }

    this._dialogService
      .confirm({
        title: 'accounts.logoutConfirmation.title',
        message: 'accounts.logoutConfirmation.body',
      })
      .subscribe((confirmed) => {
        if (confirmed) this.identityService.clearIdentity();
      });
  }
}
